/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import { SingleLineHealthEntrySummary, ObservationSummary, TrackableSummary, FileViewHealthEntrySummary, FileViewHealthEntryValueSummary, SingleLineTrackableSummary, SingleLineObservationSummary } from './OccurrenceSummary';

import { Card, Icons } from '../../../../bphComponents/bundle';

import RouteNames from '../../../appAuthorized/RouteNames';
import { useAttachmentsForFile } from '../../../hooks/useAttachments';
import { useNavigate } from '../../../hooks/useNavigate';
import { useHealthEntriesForActiveProfile, useHealthEntryValueForActiveProfile } from '../../../hooks/useHealthEntry';
import COLORS from '../../../styles/Colors';
import healthEntryUtility from '../../../../models/HealthEntryUtility';
import { useTrackableListForActiveProfile } from '../../../hooks/useTrackable';
import { attachmentTypes } from '../../../../models/AttachmentModel';

const AttachedHealthEntry = (props) => {
  const { attachment, languageCode } = props;
  const [{ healthEntryLookup }] = useHealthEntriesForActiveProfile();
  const heId = attachment.parent.id;
  const he = healthEntryLookup[heId];

  return (
    <FileViewHealthEntrySummary
      healthEntryData={he}
      languageCode={languageCode}
    />
  );
};

const AttachedHealthEntryValue = (props) => {
  const { attachment, languageCode } = props;
  return (
    <FileViewHealthEntryValueSummary
      heValueId={attachment.parent.id}
      languageCode={languageCode}
    />
  );
};

const AttachedTrackable = (props) => {
  const { trackableId, languageCode } = props;
  const [{ trackableLookup, trackableTermLookup }] = useTrackableListForActiveProfile();

  return (
    <TrackableSummary
      trackable={trackableLookup[trackableId]}
      terms={trackableTermLookup}
      languageCode={languageCode}
      showDate={true}
    />
  );
};

const AttachedObservation = (props) => {
  const { observationId, languageCode, showDate } = props;
  const [{ observationLookup, observationToTrackableId, trackableLookup, trackableTermLookup }] = useTrackableListForActiveProfile();
  const trackableId = observationToTrackableId[observationId];

  return (
    <ObservationSummary
      trackable={trackableLookup[trackableId]}
      observation={observationLookup[observationId]}
      terms={trackableTermLookup}
      languageCode={languageCode}
      showDate={showDate}
    />
  );
};

const SingleLineAttachedHealthEntry = (props) => {
  const { attachment, languageCode } = props;
  const [{ healthEntryLookup }] = useHealthEntriesForActiveProfile();
  const heId = attachment.parent.id;
  const he = healthEntryLookup[heId];

  return (
    <SingleLineHealthEntrySummary
      healthEntryData={he}
      healthEntryValueData={null}
      languageCode={languageCode}
    />
  );
};

const SingleLineAttachedHealthEntryValue = (props) => {
  const { attachment, languageCode } = props;
  const hevId = attachment.parent.id;
  const [{ healthEntryValue, parentHealthEntry }] = useHealthEntryValueForActiveProfile(hevId);

  return (
    <SingleLineHealthEntrySummary
      healthEntryData={parentHealthEntry}
      healthEntryValueData={healthEntryValue}
      languageCode={languageCode}
    />
  );
};

const SingleLineAttachedTrackable = (props) => {
  const { trackableId, languageCode } = props;
  const [{ trackableLookup, trackableTermLookup }] = useTrackableListForActiveProfile();

  return (
    <SingleLineTrackableSummary
      trackable={trackableLookup[trackableId]}
      terms={trackableTermLookup}
      languageCode={languageCode}
    />
  );
};

const SingleLineAttachedObservation = (props) => {
  const { observationId, languageCode } = props;
  const [{ observationLookup, observationToTrackableId, trackableLookup, trackableTermLookup }] = useTrackableListForActiveProfile();
  const trackableId = observationToTrackableId[observationId];
  if (!trackableId) { return null; }

  return (
    <SingleLineObservationSummary
      trackable={trackableLookup[trackableId]}
      observation={observationLookup[observationId]}
      terms={trackableTermLookup}
      languageCode={languageCode}
    />
  );
};

export const ParentAttachmentSingleLineSummary = (props) => {
  const { languageCode, attachment } = props;
  const parentAtt = attachment.parent;

  switch (parentAtt.type) {
    case 'health_entry_value':
      return (
        <SingleLineAttachedHealthEntryValue
          attachment={attachment}
          languageCode={languageCode}
        />
      );
    case 'health_entry':
      return (
        <SingleLineAttachedHealthEntry
          attachment={attachment}
          languageCode={languageCode}
        />
      );
    case 'trackable':
      return (
        <SingleLineAttachedTrackable
          trackableId={parentAtt.id}
          languageCode={languageCode}
          showDate={true}
        />
      );
    case 'trackable_observation':
      return (
        <SingleLineAttachedObservation
          observationId={parentAtt.id}
          languageCode={languageCode}
          showDate={true}
        />
      );
    default:
      return null;
  }
};

export const ParentAttachmentSummary = (props) => {
  const { languageCode, attachment } = props;
  const parentAtt = attachment.parent;

  switch (parentAtt.type) {
    case 'health_entry_value':
      return (
        <AttachedHealthEntryValue
          attachment={attachment}
          languageCode={languageCode}
        />
      );
    case 'health_entry':
      return (
        <AttachedHealthEntry
          attachment={attachment}
          languageCode={languageCode}
        />
      );
    case 'trackable':
      return (
        <AttachedTrackable
          trackableId={parentAtt.id}
          languageCode={languageCode}
          showDate={true}
        />
      );
    case 'trackable_observation':
      return (
        <AttachedObservation
          observationId={parentAtt.id}
          languageCode={languageCode}
          showDate={true}
        />
      );
    default:
      return null;
  }
};

interface Props {
  fileId: number;
  languageCode: string;
}
export const AttachmentsForFile = (props: Props) => {
  const { fileId, languageCode } = props;
  const [{ healthEntryLookup, heValueLookup, heValueToHeLookup, ...heData }] = useHealthEntriesForActiveProfile();

  const [{ hasAttachments, isLoaded }, attachmentActions] = useAttachmentsForFile(fileId);
  const [, navActions] = useNavigate();

  if (!(isLoaded && hasAttachments && heData.isLoaded)) { return null; }

  const sortedAttachments = attachmentActions.getSortedAttachments();
  const standardCategoryOrder = healthEntryUtility.standardCategoryOrder;
  return (
    <>
      <Card.ExternalCardTitle className="font-color-gsLightest">
        <FormattedMessage id="files.attachedToList.label" />
        <Icons.IconFile.Small color={COLORS.gsLightest} />
      </Card.ExternalCardTitle>
      <Card.CardShape>
        <ul>
          {
            sortedAttachments
              .map(item => {
                const { attachment, category, healthEntry, healthEntryValue, trackable, observation } = item;
                if (!category) { return; }

                return (
                  <Card.CardContent
                    key={attachment.id}
                    hasBottomBorder={true}
                    onClick={() => {
                      switch (attachment.parent.type) {
                        case attachmentTypes.healthEntryValue:
                          navActions.goTo(RouteNames.healthEntryValue(healthEntry.id, healthEntryValue.id));
                          break;
                        case attachmentTypes.healthEntry:
                          navActions.goTo(RouteNames.healthEntry(healthEntry.id));
                          break;
                        case attachmentTypes.trackable:
                          navActions.goTo(RouteNames.trackableObservationList(trackable.id));
                          break;
                        case attachmentTypes.trackableObservation:
                          navActions.goTo(RouteNames.trackableObservation(trackable.id, observation.id));
                          break;
                        default:
                          break;
                      }
                    }}
                  >
                    <ParentAttachmentSummary
                      attachment={item.attachment}
                      languageCode={languageCode}
                    />
                  </Card.CardContent>
                );
              })
          }
        </ul>
      </Card.CardShape>
    </>
  );
};
